import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { prop } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { clsb, clsx } from 'js/utils/classNames.util';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from './helpers';
import * as constants from './constants';
import { withFinalFormField } from '../../hocs';
import { SelectPure as Select } from '../Select';
import { getInteractionClass } from '../../helpers';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './DatePicker.scss';

export const DatePickerBare: React.FC<Types.IDatePickerProps> = ({
    id,
    hint,
    meta,
    label,
    value,
    onBlur,
    onFocus,
    onChange,
    disabled,
    readOnly,
    className,
    classNames,
    infoTooltip,
    yearRange = 'future',
    yearRangeNumber = 10,
    isLabelAsteriskEnabled
}) => {
    const idRef = useRef<string>(id || uuid());
    const translate = useTranslations();
    const dateFormat = useAppSelector(prop('dateFormat'));
    const [stateDate, setStateDate] = useState<Date | null>(null);
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const [stateSelectData, setStateSelectData] = useState<Types.IDatePickerState>(constants.DEFAULT_DATE_PICKER_STATE);
    const dateParts = dateFormat.split(/[-/]/);

    const selectClassNames = useMemo(
        () => ({
            ...classNames,
            container: clsb([style, classNames], ['selectPureContainer']),
            innerContainer: clsb([style, classNames], ['selectPureInnerContainer'])
        }),
        [classNames]
    );

    const interactionClass = getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    useEffect(() => {
        stateDate && onChange && onChange({ target: { value: stateDate.valueOf() } });
    }, [onChange, stateDate]);

    useEffect(() => {
        setStateSelectData({
            stateSelectDayValue: helpers.getSelectDataByTimestamp('day', value),
            stateSelectMonthValue: helpers.getSelectDataByTimestamp('month', value),
            stateSelectYearValue: helpers.getSelectDataByTimestamp('year', value),
            stateSelectDayOptions: helpers.getSelectDayOptions({ timestamp: value }),
            stateSelectMonthOptions: helpers.getSelectMonthOptions(),
            stateSelectYearOptions: helpers.getSelectYearOptions(yearRange, yearRangeNumber)
        });
    }, [value, yearRange, yearRangeNumber]);

    const onSelectChange = useCallback(
        (type: Types.TSelectType) =>
            (event: React.ChangeEvent<HTMLSelectElement>): void => {
                const selectFieldValue = event.target?.value;
                const { stateSelectDayValue, stateSelectMonthValue, stateSelectYearValue } = stateSelectData;

                switch (type) {
                    case 'day': {
                        setStateSelectData((prevState) => ({ ...prevState, stateSelectDayValue: selectFieldValue }));

                        if (stateSelectMonthValue && stateSelectYearValue) {
                            setStateDate(
                                new Date(Number(stateSelectYearValue), Number(stateSelectMonthValue), Number(selectFieldValue))
                            );
                        }

                        break;
                    }
                    case 'month': {
                        const selectDayValue = helpers.getSelectDayValue({
                            stateSelectDayValue,
                            stateSelectYearValue,
                            stateSelectMonthValue: selectFieldValue
                        });

                        setStateSelectData((prevState) => ({
                            ...prevState,
                            stateSelectDayValue: selectDayValue,
                            stateSelectMonthValue: selectFieldValue,
                            stateSelectDayOptions: helpers.getSelectDayOptions({
                                stateSelectMonthValue: selectFieldValue,
                                stateSelectYearValue
                            })
                        }));

                        if (selectDayValue && stateSelectYearValue) {
                            setStateDate(
                                new Date(Number(stateSelectYearValue), Number(selectFieldValue), Number(selectDayValue))
                            );
                        }

                        break;
                    }
                    case 'year': {
                        const selectDayValue = helpers.getSelectDayValue({
                            stateSelectDayValue,
                            stateSelectMonthValue,
                            stateSelectYearValue: selectFieldValue
                        });

                        setStateSelectData((prevState) => ({
                            ...prevState,
                            stateSelectDayValue: selectDayValue,
                            stateSelectYearValue: selectFieldValue,
                            stateSelectDayOptions: helpers.getSelectDayOptions({
                                stateSelectMonthValue,
                                stateSelectYearValue: selectFieldValue
                            })
                        }));

                        if (stateSelectMonthValue && selectDayValue) {
                            setStateDate(
                                new Date(Number(selectFieldValue), Number(stateSelectMonthValue), Number(selectDayValue))
                            );
                        }

                        break;
                    }
                    default: {
                        break;
                    }
                }
            },
        [stateSelectData, setStateSelectData]
    );

    return (
        <div onBlur={onBlur} onFocus={onFocus} className={clsx([clsb([style, classNames], ['container']), interactionClass, className])}>
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    label={label}
                    readOnly={readOnly}
                    id={idRef.current}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}
            <div className={clsb([style, classNames], ['selectContainer'])}>
                {
                    dateParts.map((datePart) => {
                        switch (datePart) {
                            case constants.DATE_PART.year:
                                return (
                                    <Select
                                        name="year"
                                        key={datePart}
                                        readOnly={readOnly}
                                        disabled={isDisabled}
                                        classNames={selectClassNames}
                                        onChange={onSelectChange('year')}
                                        value={stateSelectData.stateSelectYearValue}
                                        options={stateSelectData.stateSelectYearOptions}
                                        aria-label={translate('playtech.mobile.system.year')}
                                        placeholder={translate('playtech.mobile.system.year')}
                                    />
                                );
                            case constants.DATE_PART.month:
                                return (
                                    <Select
                                        name="month"
                                        key={datePart}
                                        readOnly={readOnly}
                                        disabled={isDisabled}
                                        classNames={selectClassNames}
                                        onChange={onSelectChange('month')}
                                        value={stateSelectData.stateSelectMonthValue}
                                        options={stateSelectData.stateSelectMonthOptions}
                                        aria-label={translate('playtech.mobile.system.month')}
                                        placeholder={translate('playtech.mobile.system.month')}
                                    />
                                );
                            case constants.DATE_PART.day:
                                return (
                                    <Select
                                        name="day"
                                        key={datePart}
                                        id={idRef.current}
                                        readOnly={readOnly}
                                        disabled={isDisabled}
                                        classNames={selectClassNames}
                                        onChange={onSelectChange('day')}
                                        value={stateSelectData.stateSelectDayValue}
                                        options={stateSelectData.stateSelectDayOptions}
                                        aria-label={translate('playtech.mobile.system.day')}
                                        placeholder={translate('playtech.mobile.system.day')}
                                    />
                                );
                            default:
                                return null;
                        }
                    })
                }
            </div>
            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const DatePickerPure = memo(DatePickerBare);
export const DatePicker = withFinalFormField<number>()(DatePickerPure);
